import axios from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowRight, CheckCircle2, GitCompareArrows, ImageOff, Loader2, RefreshCw, ShieldCheck, UploadCloud } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { useToast } from '../components/ui/toast'
import {
  applyContentPromotion,
  getContentPromotionPreview,
  type ContentPromotionApplyResult,
  type ContentPromotionPreview,
  type ContentPromotionRecord
} from '../lib/api/contentPromotion'
import { cn } from '../utils/cn'

const CONFIRMATION = 'SYNC STAGING TO PRODUCTION'

const statusStyles: Record<ContentPromotionRecord['status'], string> = {
  create: 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  update: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  unchanged: 'border-border bg-muted text-muted-foreground',
  incomplete: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  productionOnly: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  blocked: 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300'
}

const statusLabels: Record<ContentPromotionRecord['status'], string> = {
  create: 'Create in production',
  update: 'Update production',
  unchanged: 'Already in sync',
  incomplete: 'Staging incomplete',
  productionOnly: 'Production-only · preserved',
  blocked: 'Blocked'
}

const kindLabels: Record<ContentPromotionRecord['kind'], string> = {
  operator: 'Operator',
  tour: 'Tour',
  popularHike: 'Popular hike',
  badge: 'Badge',
  hikeStrongChallenge: 'HikeStrong Challenge',
  seasonalChallenge: 'Seasonal Challenge',
  exercise: 'Exercise',
  exerciseVideo: 'Exercise video',
  fitnessHikeTarget: 'General Fitness level'
}

function errorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    return (error.response?.data as { message?: string } | undefined)?.message ?? error.message
  }
  return error instanceof Error ? error.message : 'Unknown error'
}

function Metric({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <Card>
      <CardContent className="py-5">
        <div className={cn('text-3xl font-black tracking-tight text-foreground', tone)}>{value}</div>
        <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  )
}

function RecordRow({ record }: { record: ContentPromotionRecord }) {
  return (
    <details className="group rounded-xl border border-border bg-card open:shadow-sm">
      <summary className="flex cursor-pointer list-none flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{kindLabels[record.kind]}</span>
            <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide', statusStyles[record.status])}>
              {statusLabels[record.status]}
            </span>
          </div>
          <h3 className="mt-1 truncate font-semibold text-foreground">{record.name}</h3>
          {record.operatorName && <p className="text-xs text-muted-foreground">{record.operatorName}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground">
          {record.kind !== 'exercise' && record.kind !== 'fitnessHikeTarget' && <>
            <span>{record.sourceImageCount} staging {record.kind === 'exerciseVideo' ? 'thumbnail' : 'image'}{record.sourceImageCount === 1 ? '' : 's'}</span>
            <ArrowRight className="h-3.5 w-3.5" />
            <span>{record.targetImageCount} live</span>
          </>}
        </div>
      </summary>
      <div className="grid gap-4 border-t border-border px-4 py-4 text-sm md:grid-cols-3">
        <div>
          <div className="font-semibold text-foreground">Will change</div>
          <p className="mt-1 text-muted-foreground">{record.changedFields.length ? record.changedFields.join(', ') : 'Nothing'}</p>
        </div>
        <div>
          <div className="font-semibold text-foreground">Will preserve</div>
          <p className="mt-1 text-muted-foreground">{record.preservedFields.length ? record.preservedFields.join(', ') : 'No field exceptions'}</p>
        </div>
        <div>
          <div className="font-semibold text-foreground">Review notes</div>
          {record.warnings.length ? (
            <ul className="mt-1 space-y-1 text-muted-foreground">
              {record.warnings.map((warning) => <li key={warning}>• {warning}</li>)}
            </ul>
          ) : <p className="mt-1 text-muted-foreground">Ready to promote</p>}
        </div>
      </div>
    </details>
  )
}

export const ContentPromotionPage = () => {
  const { addToast } = useToast()
  const [preview, setPreview] = useState<ContentPromotionPreview | null>(null)
  const [result, setResult] = useState<ContentPromotionApplyResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [confirmation, setConfirmation] = useState('')

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setPreview(await getContentPromotionPreview())
    } catch (requestError) {
      setError(errorMessage(requestError))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  const groupedRecords = useMemo(() => {
    const records = preview?.records ?? []
    return [
      { id: 'action', label: 'Ready to publish', records: records.filter((record) => record.status === 'create' || record.status === 'update') },
      { id: 'blocked', label: 'Needs review', records: records.filter((record) => record.status === 'blocked') },
      { id: 'exceptions', label: 'Staging fields not promotable', records: records.filter((record) => record.status === 'incomplete') },
      { id: 'preserved', label: 'Production content preserved', records: records.filter((record) => record.status === 'productionOnly') }
    ].filter((group) => group.records.length > 0)
  }, [preview])

  const handleApply = async () => {
    if (!preview) return
    setApplying(true)
    try {
      const applied = await applyContentPromotion(preview.changeToken, confirmation)
      setResult(applied)
      setPreview(applied.readback)
      setDialogOpen(false)
      setConfirmation('')
      addToast({ title: 'Staging content promoted', description: `${applied.created} created and ${applied.updated} updated.`, variant: 'success' })
    } catch (requestError) {
      addToast({ title: 'Promotion was not applied', description: errorMessage(requestError), variant: 'error' })
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-accent">
            <GitCompareArrows className="h-4 w-4" /> Content operations
          </div>
          <h1 className="text-3xl font-black tracking-tight text-foreground">Content Promotion</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Compare staging badges, challenges, operators, tours, popular hikes, exercises, exercise videos, and General Fitness levels with production, then publish reviewed staging-owned content and media links.
            Production-only records and live-only fields are never deleted.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refresh} disabled={loading || applying}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh comparison
          </Button>
          {preview?.currentEnvironment === 'production' && (
            <Button onClick={() => setDialogOpen(true)} disabled={!preview.canApply || !preview.isConfigured || preview.summary.blocked > 0 || preview.summary.creates + preview.summary.updates === 0 || applying}>
              <UploadCloud className="mr-2 h-4 w-4" /> Sync to production
            </Button>
          )}
        </div>
      </div>

      {preview && (
        <div className={cn('flex items-start gap-3 rounded-xl border p-4', preview.currentEnvironment === 'production' ? 'border-red-500/30 bg-red-500/5' : 'border-amber-500/30 bg-amber-500/5')}>
          {preview.currentEnvironment === 'production' ? <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-red-600" /> : <GitCompareArrows className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />}
          <div>
            <div className="font-semibold text-foreground">
              {preview.currentEnvironment === 'production' ? 'Production publishing control' : 'Staging preview · read only'}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {preview.currentEnvironment === 'production'
                ? preview.canApply ? 'You are authorized to publish this reviewed staging snapshot.' : 'This account may compare content but is not authorized to sync.'
                : 'Review the exact production impact here. Open production Base Camp when you are ready to sync.'}
            </p>
          </div>
        </div>
      )}

      {result && (
        <section className="space-y-3">
          <Card className="border-emerald-500/30 bg-emerald-500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="h-5 w-5" /> Production readback complete</CardTitle>
              <CardDescription>{result.created} records created and {result.updated} updated by {result.appliedBy}. The comparison below is the post-sync production readback.</CardDescription>
            </CardHeader>
          </Card>
          <details className="rounded-xl border border-emerald-500/30 bg-card" open>
            <summary className="cursor-pointer p-4 font-semibold text-foreground">Exact records changed · {result.changedRecords.length}</summary>
            <div className="space-y-2 border-t border-border p-3">
              {result.changedRecords.map((record) => <RecordRow key={`applied-${record.kind}-${record.sourceId}`} record={record} />)}
            </div>
          </details>
        </section>
      )}

      {error && <Card className="border-red-500/30"><CardContent className="flex gap-3 py-5 text-sm text-red-600"><AlertTriangle className="h-5 w-5 shrink-0" />{error}</CardContent></Card>}

      {loading ? (
        <Card><CardContent className="flex items-center gap-2 py-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Comparing staging with production…</CardContent></Card>
      ) : preview && !preview.isConfigured ? (
        <Card className="border-amber-500/30"><CardHeader><CardTitle>Peer environment not configured</CardTitle><CardDescription>{preview.configurationMessage}</CardDescription></CardHeader></Card>
      ) : preview && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <Metric label="Create" value={preview.summary.creates} tone="text-sky-600" />
            <Metric label="Update" value={preview.summary.updates} tone="text-amber-600" />
            <Metric label="Already synced" value={preview.summary.unchanged} />
            <Metric label="Live preserved" value={preview.summary.productionOnlyPreserved} tone="text-emerald-600" />
            <Metric label="Blocked" value={preview.summary.blocked} tone={preview.summary.blocked ? 'text-red-600' : undefined} />
            <Metric label="Missing images" value={preview.summary.missingSourceImages} tone={preview.summary.missingSourceImages ? 'text-amber-600' : undefined} />
          </div>

          {preview.summary.creates + preview.summary.updates === 0 && preview.summary.blocked === 0 && (
            <Card><CardContent className="flex items-center gap-3 py-8"><CheckCircle2 className="h-6 w-6 text-emerald-600" /><div><div className="font-semibold text-foreground">Production is in sync</div><div className="text-sm text-muted-foreground">All safely promotable staging content matches production.</div></div></CardContent></Card>
          )}

          {groupedRecords.map((group) => (
            <section key={group.id} className="space-y-3">
              <div className="flex items-center gap-2">
                {group.id === 'blocked' || group.id === 'exceptions' ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : group.id === 'preserved' ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <UploadCloud className="h-4 w-4 text-primary" />}
                <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">{group.label} · {group.records.length}</h2>
              </div>
              <div className="space-y-2">{group.records.map((record) => <RecordRow key={`${record.kind}-${record.sourceId || record.targetId}`} record={record} />)}</div>
            </section>
          ))}

          {(preview.summary.missingSourceImages > 0 || preview.summary.imageConflicts > 0) && (
            <Card className="border-amber-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base"><ImageOff className="h-5 w-5 text-amber-600" /> Image exceptions remain visible</CardTitle>
                <CardDescription>{preview.summary.missingSourceImages} staging records have no image; {preview.summary.imageConflicts} production image sets differ and will be preserved.</CardDescription>
              </CardHeader>
            </Card>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setConfirmation('') }}>
        <DialogContent className="max-w-xl p-6">
          <DialogTitle>Publish staging content to production?</DialogTitle>
          <DialogDescription className="mt-2">
            This will create {preview?.summary.creates ?? 0} and update {preview?.summary.updates ?? 0} records. Exercise video links and thumbnails follow staging. General Fitness levels link to the matching production hikes. Production-only records, referral codes, favorites, selection counts, and popular hike thumbnails remain untouched.
          </DialogDescription>
          <div className="mt-5 space-y-2">
            <label htmlFor="promotion-confirmation" className="text-sm font-semibold text-foreground">Type <span className="font-mono">{CONFIRMATION}</span></label>
            <Input id="promotion-confirmation" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={applying}>Cancel</Button>
            <Button variant="destructive" onClick={handleApply} disabled={confirmation !== CONFIRMATION || applying}>
              {applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />} Publish to production
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
